import React, { useEffect, useCallback, useState, useMemo } from 'react';
import {
  Col,
  Table,
  Input,
  Button,
  Select,
  Row,
  Switch,
  Tag,
  Tooltip,
  Badge,
  Pagination,
  message,
  Dropdown,
  Checkbox,
  Card,
  DatePicker,
} from 'antd';
import {
  taskTypes,
  statusTypes,
  DEFAULT_STATUSES,
  subTypes,
  urgencyTypes,
  urgencyColors,
  advertiserTypes,
  filterMenuColumnOptions,
} from '../configs/task.config';
import {
  LeftOutlined,
  RightOutlined,
  SyncOutlined,
  FieldTimeOutlined,
  DownOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import {
  getNumberWithSuffix,
  getLastCommentUser,
  isUserAssignee,
  deduplicateUsers,
} from '../../../../platform/ux/Tasks/utils/task.utils';
import { useTaskContext } from '../../../../core/components/tasks/useTaskContext';
import { withRouter, Link } from 'react-router-dom';
import { useUserContext } from '../../../../core/components/users/useUserContext';
import { debounce } from 'lodash';
import qs from 'qs';
import useSearchParams from '../../../../core/hooks/useSearchParams';
import AvatarS3 from '../../../shared/images/AvatarS3';
import { CloseOutlined } from '@ant-design/icons';
import { startCase } from 'lodash';
import { TaskRecentActivity } from './TaskRecentActivity';
import '../styles/SelectInput.css';
import '../styles/CustomTable.css';
import '../styles/CustomPagination.css';
import '../styles/SwitchButton.css';
import { useOrgContext } from '../../../../core/components/orgs/useOrgContext';
import { getRelativeTime } from '../../../shared/GetRelativeTime';
import '../styles/Badge.css';
import Kanban from './Kanban';
import moment from 'moment';

const { Option, OptGroup } = Select;

const TaskBoard = ({ history, location }) => {
  const {
    currentUser,
    fetchTasksPaginated,
    task,
    taskBoardPagination,
    setTaskBoardPagination,
    tasksLoading,
    showMyTasks,
    setShowMyTasks,
    isKanban,
    setIsKanban,
    viewChanged,
    setViewChanged,
    setTask,
  } = useTaskContext();
  const {
    fetchBasicUsersDetails,
    basicUsersDetails,
    basicUsersDetailsLoading,
    fetchRequestorUsers,
    requestorData,
    requestorLoading,
  } = useUserContext();
  const { fetchBasicOrgsDetails, basicOrgsDetails, basicOrgsDetailsLoading } = useOrgContext();
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams({
    assigneeNames: '',
    requestor: '',
    requestTypes: '',
    requestSubTypes: '',
    status: '',
    advertiserTypes: '',
    advertiser: '',
    name: '',
    urgency: '',
    showMyTasks: showMyTasks,
    following: visibleColumns ? visibleColumns.includes('following') : false,
    collaborating: visibleColumns ? visibleColumns.includes('collaborating') : false,
    dueDate: '',
    kanban: isKanban,
    showAllStatuses: true,
  });

  const handleAssigneeFilter = (selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      assigneeNames: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleRequestorFilter = (selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      requestor: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleTaskTypeFilter = (selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      requestTypes: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleTaskSubTypeFilter = (selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      requestSubTypes: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleStatusFilter = (selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      status: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleAdvertiserTypeFilter = (selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      advertiserTypes: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleAdvertiserFilter = (selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      advertiser: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleUrgencyFilter = (selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      urgency: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
  };

  // New: Due Date Filter
  const handleDueDateFilter = (selectedValue) => {
    let dueDateValue = selectedValue || '';
    // Always set start to T00:00:00 and end to T23:59:59 if a range is selected
    if (dueDateValue && dueDateValue.split && dueDateValue.split(',').length === 2) {
      let [from, to] = dueDateValue.split(',');
      from = from.split('T')[0];
      to = to.split('T')[0];
      dueDateValue = `${from}T00:00:00,${to}T23:59:59`;
    }
    const updatedFilters = {
      ...searchParams,
      dueDate: dueDateValue,
    };
    setSearchParams(updatedFilters);
    setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleFetchTasksPaginated = debounce(
    ({
      skip,
      first,
      sortField = null,
      sortOrder = 'updatedAt_DESC',
      name = '',
      assigneeNames = '',
      requestor = '',
      requestTypes = '',
      requestSubTypes = '',
      status = '',
      advertiserTypes = '',
      advertiser = '',
      urgency = '',
      dueDate = '',
      showAllStatuses = true,
    }) => {
      const assigneeNamesArray = assigneeNames ? assigneeNames.split(',') : [];
      const requestorArray = requestor ? requestor.split(',') : [];
      const requestTypesArray = requestTypes ? requestTypes.split(',') : [];
      const requestSubTypesArray = requestSubTypes ? requestSubTypes.split(',') : [];
      const statusArray = status ? status.split(',') : [];
      const advertiserTypesArray = advertiserTypes ? advertiserTypes.split(',') : [];
      const advertiserArray = advertiser ? advertiser.split(',') : [];
      const urgencyArray = urgency ? urgency.split(',') : [];
      // Due date filter (range or single day)
      let dueDateStart = null;
      let dueDateEnd = null;
      if (dueDate && dueDate.split(',').length === 2) {
        [dueDateStart, dueDateEnd] = dueDate.split(',');
      }
      const following = visibleColumns ? visibleColumns.includes('following') : false;
      const collaborating = visibleColumns ? visibleColumns.includes('collaborating') : false;
      const orderBy =
        sortOrder === 'updatedAt_DESC'
          ? 'updatedAt_DESC'
          : sortField
            ? `${sortField}_${sortOrder}`
            : 'updatedAt_DESC';

      fetchTasksPaginated({
        variables: {
          where: {
            AND: [
              { name_contains: name },
              ...(showMyTasks && currentUser.isPreviewOrg
                ? [{ id: 'no-mytasks-in-preview-mode' }]
                : showMyTasks
                  ? [
                      {
                        OR: [
                          { assigneeUser: { id: currentUser.id } },
                          { assigneeUsers_some: { id: currentUser.id } },
                          { createdBy: { id: currentUser.id } },
                          { followers_some: { id: currentUser.id } },
                          { collaborators_some: { id: currentUser.id } },
                        ],
                      },
                    ]
                  : []),
              ...(assigneeNamesArray.length
                ? [
                    {
                      OR: [
                        {
                          assigneeUsers_some: {
                            id_in: assigneeNamesArray,
                          },
                        },
                        { assigneeUser: { id_in: assigneeNamesArray } },
                      ],
                    },
                  ]
                : []),
              ...(requestorArray.length
                ? [
                    {
                      createdBy: {
                        id_in: requestorArray,
                      },
                    },
                  ]
                : []),
              ...(requestTypesArray.length ? [{ requestType_in: requestTypesArray }] : []),
              ...(requestSubTypesArray.length ? [{ requestSubType_in: requestSubTypesArray }] : []),
              ...(!isKanban
                ? statusArray.length
                  ? [{ status_in: statusArray }]
                  : showAllStatuses === true || showAllStatuses === 'true'
                    ? []
                    : [{ status_in: DEFAULT_STATUSES }]
                : []),
              ...(advertiserTypesArray.length
                ? [
                    {
                      createdBy: {
                        roles_some: { org: { level_in: advertiserTypesArray } },
                      },
                    },
                  ]
                : []),
              ...(advertiserArray.length
                ? [
                    {
                      createdBy: {
                        roles_some: { org: { name_in: advertiserArray } },
                      },
                    },
                  ]
                : []),
              ...(urgencyArray.length ? [{ urgency_in: urgencyArray }] : []),
              ...(dueDateStart && dueDateEnd
                ? [
                    {
                      dueDate_gte: dueDateStart,
                      dueDate_lte: dueDateEnd,
                    },
                  ]
                : []),
              ...(following || collaborating
                ? [
                    {
                      OR: [
                        ...(following ? [{ followers_some: { id: currentUser.id } }] : []),
                        ...(collaborating ? [{ collaborators_some: { id: currentUser.id } }] : []),
                      ],
                    },
                  ]
                : []),
              {
                OR: [
                  {
                    createdBy: {
                      roles_some: {
                        org: {
                          OR: [
                            {
                              parentOrg_some: {
                                id: currentUser.role.org.id,
                              },
                            },
                            {
                              id: currentUser.role.org.id,
                            },
                          ],
                        },
                      },
                    },
                  },
                ],
              },
            ],
          },
          orderBy,
          skip,
          first,
        },
      });
    },
    500
  );

  const fetchBasicUsers = useCallback(
    debounce((name = '') => {
      fetchBasicUsersDetails({
        variables: {
          first: 50,
          skip: 0,
          where: {
            AND: [
              {
                assignedUserTasks_some: {
                  createdBy: {
                    roles_some: {
                      org: {
                        OR: [
                          { id: currentUser.role.org.id },
                          { parentOrg_some: { id: currentUser.role.org.id } },
                        ],
                      },
                    },
                  },
                },
              },
              {
                roles_some: {
                  roleItems_some: { feature: 'TASK_MANAGEMENT' },
                },
              },
              { name_contains: name },
            ],
          },
        },
      });
    }, 500),
    [currentUser.role.org.id]
  );

  const fetchBasicUsersRequestor = useCallback(
    debounce((name = '') => {
      fetchRequestorUsers({
        variables: {
          first: 50,
          skip: 0,
          where: {
            AND: [
              {
                createdTasks_some: {
                  createdBy: {
                    roles_some: {
                      org: {
                        OR: [
                          { id: currentUser.role.org.id },
                          { parentOrg_some: { id: currentUser.role.org.id } },
                        ],
                      },
                    },
                  },
                },
              },
              {
                roles_some: {
                  roleItems_some: { feature: 'TASK_MANAGEMENT' },
                },
              },
              { name_contains: name },
            ],
          },
        },
      });
    }, 500),
    [currentUser.role.org.id]
  );

  const fetchAdvertiserOrgs = debounce((name = '') => {
    fetchBasicOrgsDetails({
      variables: {
        first: 1000,
        skip: 0,
        where: {
          AND: [
            {
              OR: [
                {
                  assignedTasks_some: {
                    createdBy: {
                      roles_some: {
                        org: {
                          OR: [
                            { id: currentUser.role.org.id },
                            { parentOrg_some: { id: currentUser.role.org.id } },
                          ],
                        },
                      },
                    },
                  },
                },
                {
                  channelPartnerTasks_some: {
                    createdBy: {
                      roles_some: {
                        org: {
                          OR: [
                            { id: currentUser.role.org.id },
                            { parentOrg_some: { id: currentUser.role.org.id } },
                          ],
                        },
                      },
                    },
                  },
                },
              ],
            },
            {
              roles_some: {
                roleItems_some: { feature: 'TASK_MANAGEMENT' },
              },
              name_contains: name,
            },
          ],
        },
      },
    });
  }, 500);

  const handlePaginationChange = (params) => {
    if (isKanban) return; // Prevent pagination logic in Kanban mode
    const updatedPagination = {
      ...taskBoardPagination,
      current: params.page || taskBoardPagination.current,
      pageSize: params.pageSize || taskBoardPagination.pageSize,
    };

    const sortOrder =
      params.sortOrder === 'ascend'
        ? 'ASC'
        : params.sortOrder === 'descend'
          ? 'DESC'
          : 'updatedAt_DESC';

    const sortField =
      sortOrder === 'updatedAt_DESC' ? null : params.sortField || taskBoardPagination.sortField;

    setTaskBoardPagination(updatedPagination);

    handleFetchTasksPaginated({
      skip: (updatedPagination.current - 1) * updatedPagination.pageSize || 0,
      first: updatedPagination.pageSize,
      sortField,
      sortOrder,
      ...searchParams,
    });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    if (isKanban) return; // Prevent table change logic in Kanban mode
    const sortField = sorter.field || null;
    const sortOrder = sorter.order || null;

    handlePaginationChange({
      page: 1,
      sortField,
      sortOrder,
    });
  };

  const handlePaginationSizeChange = (current, pageSize) => {
    if (isKanban) return; // Prevent page size change in Kanban mode
    handlePaginationChange({
      page: 1,
      pageSize,
    });
  };

  const itemRender = (page, type, originalElement) => {
    if (type === 'prev') {
      return (
        <Button icon={<LeftOutlined />} type="text">
          Previous
        </Button>
      );
    }
    if (type === 'next') {
      return (
        <Button type="text">
          Next
          <RightOutlined />
        </Button>
      );
    }
    return originalElement;
  };

  const uniqueUsers = useMemo(() => deduplicateUsers(basicUsersDetails), [basicUsersDetails]);
  const uniqueRequestors = useMemo(() => deduplicateUsers(requestorData), [requestorData]);

  useEffect(() => {
    if (searchParams.assigneeNames === '' || searchParams.assigneeNames) {
      fetchBasicUsers();
    }
  }, [fetchBasicUsers, searchParams.assigneeNames]);

  useEffect(() => {
    if (searchParams.requestor === '' || searchParams.requestor) {
      fetchBasicUsersRequestor();
    }
  }, [fetchBasicUsersRequestor, searchParams.requestor]);

  useEffect(() => {
    if (searchParams.advertiser === '') {
      fetchAdvertiserOrgs();
    }
  }, [fetchAdvertiserOrgs, searchParams.advertiser]);

  useEffect(() => {
    if (viewChanged) {
      setTask([]); // Clear tasks immediately when view changes
      setViewChanged(false); // Reset the flag
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewChanged, viewChanged]);

  useEffect(() => {
    if (isKanban) {
      // Kanban mode
      handleFetchTasksPaginated({
        skip: 0,
        first: 10000,
        ...searchParams,
      });
    } else {
      // List mode - reset to first page
      const newPagination = {
        ...taskBoardPagination,
        current: 1,
      };

      setTaskBoardPagination(newPagination);

      handleFetchTasksPaginated({
        skip: 0,
        first: newPagination.pageSize,
        ...searchParams,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isKanban, showMyTasks, searchParams]);

  const handleSaveFilters = () => {
    // Save all state including Kanban preference per user
    const stateToSave = {
      searchParams: { ...searchParams },
      taskBoardPagination,
      isKanban, // Include kanban preference in user-specific storage
      visibleColumns,
    };

    localStorage.setItem(`taskState_${currentUser.id}`, JSON.stringify(stateToSave));
    message.destroy();
    message.success('Filters saved successfully!');
  };

  const handleLoadFilters = () => {
    const savedState = localStorage.getItem(`taskState_${currentUser.id}`);
    if (!savedState) {
      message.destroy();
      message.info('No saved filters found.');
      return;
    }

    try {
      const {
        searchParams: savedSearchParams,
        taskBoardPagination: savedPagination,
        isKanban: savedIsKanban,
        visibleColumns: savedVisibleColumns,
      } = JSON.parse(savedState);

      setSearchParams(savedSearchParams);
      setTaskBoardPagination(savedPagination);

      // Restore Kanban preference if saved
      if (typeof savedIsKanban === 'boolean') {
        setIsKanban(savedIsKanban);
      }

      if (Array.isArray(savedVisibleColumns)) {
        setVisibleColumns(savedVisibleColumns);
      }

      const queryString = qs.stringify(savedSearchParams, {
        addQueryPrefix: true,
      });
      history.push(`/tasks/main/${savedPagination.current}${queryString}`);

      message.destroy();
      message.success('Filters loaded successfully!');
    } catch (error) {
      message.error('Failed to load saved filters.');
    }
  };

  const handleSearchChange = (name) => {
    const updatedFilters = { ...searchParams, name };
    setSearchParams(updatedFilters);
    setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
  };

  // Auto-load saved filters when user logs in
  useEffect(() => {
    if (currentUser && currentUser.id) {
      const savedState = localStorage.getItem(`taskState_${currentUser.id}`);
      if (savedState) {
        try {
          const {
            searchParams: savedSearchParams,
            taskBoardPagination: savedPagination,
            isKanban: savedIsKanban,
            visibleColumns: savedVisibleColumns,
          } = JSON.parse(savedState);

          // Restore user preferences from user-specific storage
          setSearchParams(savedSearchParams);
          setTaskBoardPagination(savedPagination);

          // Restore Kanban preference if saved
          if (typeof savedIsKanban === 'boolean') {
            setIsKanban(savedIsKanban);
          }

          if (Array.isArray(savedVisibleColumns)) {
            setVisibleColumns(savedVisibleColumns);
          }

          const queryString = qs.stringify(savedSearchParams, {
            addQueryPrefix: true,
          });
          history.push(`/tasks/main/${savedPagination.current}${queryString}`);
        } catch (error) {
          message.warning('Failed to load saved filters:', error);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser && currentUser.id]);

  const level =
    currentUser && currentUser.role && currentUser.role.org && currentUser.role.org.level;

  const columns = [
    {
      title: <span style={{ color: '#64748b' }}>Task Name</span>,
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (text, record) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Tooltip title={record.urgency ? `URGENCY: ${record.urgency}` : undefined}>
            <Badge
              dot
              color={urgencyColors[record.urgency] || 'white'}
              className="custom-badge"
              style={{
                marginRight: 0,
                alignSelf: 'center',
              }}
            />
          </Tooltip>
          <Link
            to={`/tasks/details/${record.id}`}
            style={{
              textDecoration: 'none',
              whiteSpace: 'normal',
              wordWrap: 'break-word',
            }}
          >
            {text}
          </Link>
        </div>
      ),
    },
    {
      title: <span style={{ color: '#64748b' }}>Due Date</span>,
      dataIndex: 'dueDate',
      key: 'dueDate',
      sorter: true,
      render: (dueDate, record) => {
        const isPastDue = new Date(dueDate) < new Date();
        const getTooltipContent = () => (
          <div>
            <div>{record.recurrenceType || '-'}</div>
            {record.weeklyDays && (
              <div>
                {record.weeklyDays
                  .map((day) => day.charAt(0) + day.slice(1).toLowerCase())
                  .join(', ')}
              </div>
            )}
            {record.dayOfMonth && record.dayOfWeek && (
              <div>
                {getNumberWithSuffix(record.dayOfMonth)} week of{' '}
                {record.dayOfWeek.charAt(0) + record.dayOfWeek.slice(1).toLowerCase()}
              </div>
            )}
            {record.dateOfMonth && <div>Every {getNumberWithSuffix(record.dateOfMonth)}</div>}
            {record.yearlyMonth && record.yearlyDate && (
              <div>
                Every {record.yearlyMonth.charAt(0) + record.yearlyMonth.slice(1).toLowerCase()}{' '}
                {getNumberWithSuffix(record.yearlyDate)}
              </div>
            )}
          </div>
        );

        return (
          <>
            <span
              style={{
                color: dueDate ? (isPastDue ? '#ff4d4f' : 'inherit') : 'inherit',
              }}
            >
              {dueDate ? new Date(dueDate).toLocaleDateString('en-CA') : 'N/A'}
            </span>
            {record.recurring && (
              <Tooltip title={getTooltipContent()}>
                <SyncOutlined style={{ marginLeft: 8, color: 'gray' }} />
              </Tooltip>
            )}
          </>
        );
      },
    },
    {
      title: <span style={{ color: '#64748b' }}>Assignee</span>,
      dataIndex: 'assigneeUsers',
      key: 'assigneeUsers',
      render: (assigneeUsers, record) => {
        const users =
          assigneeUsers && assigneeUsers.length
            ? assigneeUsers
            : record.assigneeUser
              ? [record.assigneeUser]
              : [];

        const isLastCommenterRequestor = () => {
          const lastCommentUser = getLastCommentUser(record);
          return (
            lastCommentUser && lastCommentUser.id === (record.createdBy && record.createdBy.id)
          );
        };

        if (users.length === 0) return '-';

        // Determine displayed users and remaining users
        const displayedUsers = users.slice(0, 2);
        const remainingUsers = users.length > 2 ? users.slice(2) : [];

        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              maxWidth: '400px',
            }}
          >
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                flex: 1,
              }}
            >
              {displayedUsers.map((user) => (
                <Tooltip key={user.id} title={user.name || user.username || '-'}>
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      marginLeft: -5,
                    }}
                  >
                    <AvatarS3
                      imgKey={user.avatar && user.avatar.key}
                      username={user.name || '-'}
                      avatarProps={{ size: 27 }}
                    />
                  </div>
                </Tooltip>
              ))}

              {remainingUsers.length > 0 && (
                <Tooltip
                  title={
                    <div>
                      {remainingUsers.map((user) => (
                        <div
                          key={user.id}
                          style={{
                            display: 'flex',
                            gap: 8,
                            paddingBottom: '5px',
                          }}
                        >
                          <AvatarS3
                            imgKey={user.avatar && user.avatar.key}
                            username={user.name || '-'}
                            avatarProps={{ size: 20 }}
                          />
                          {user.name || user.username || '-'}
                        </div>
                      ))}
                    </div>
                  }
                >
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      marginLeft: -5,
                    }}
                  >
                    <AvatarS3 avatarProps={{ size: 27 }} username={'+'}>
                      {remainingUsers.length}
                    </AvatarS3>
                  </div>
                </Tooltip>
              )}
            </div>
            {(isLastCommenterRequestor() ||
              !record.attachedComments ||
              (Array.isArray(record.attachedComments) && record.attachedComments.length === 0)) && (
              <div
                style={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Tooltip title="Awaiting Response">
                  <FieldTimeOutlined
                    style={{
                      fontSize: '20px',
                      color: 'gray',
                      marginRight: '5px',
                    }}
                  />
                </Tooltip>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: <span style={{ color: '#64748b' }}>Requestor</span>,
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (createdBy, record) => {
        const lastCommentUser = getLastCommentUser(record);
        const isLastCommenterAssignee = isUserAssignee(lastCommentUser, record);
        const showIcon = isUserAssignee(lastCommentUser, record, true);

        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              maxWidth: '200px',
            }}
          >
            <div
              style={{
                flex: 1,
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'normal',
              }}
            >
              {(createdBy && createdBy.name) || 'N/A'}
            </div>
            {(isLastCommenterAssignee || showIcon) && (
              <div
                style={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Tooltip title="Awaiting Response">
                  <FieldTimeOutlined
                    style={{
                      fontSize: '20px',
                      color: 'gray',
                      marginRight: '10px',
                    }}
                  />
                </Tooltip>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: <span style={{ color: '#64748b' }}>Request Type</span>,
      dataIndex: 'requestType',
      key: 'requestType',
      sorter: true,
      render: (requestType, record) => (
        <>
          {requestType || 'N/A'}
          <br />
          <span style={{ color: 'gray' }}>{record.requestSubType || ''}</span>
        </>
      ),
    },
    {
      title: <span style={{ color: '#64748b' }}>Status</span>,
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: (status) => {
        if (!status) return 'N/A';
        let formattedStatus = status
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        if (status === 'READY') formattedStatus = 'READY FOR REVIEW';
        return formattedStatus;
      },
    },
  ];

  if (level === 'CHANNEL_PARTNER') {
    columns.push({
      title: <span style={{ color: '#64748b' }}>Advertiser</span>,
      dataIndex: 'advertiser',
      key: 'advertiser',
      render: (advertiser) => (advertiser && advertiser.id ? advertiser.name : ''),
    });
  } else {
    columns.push(
      {
        title: <span style={{ color: '#64748b' }}>Advertiser Type</span>,
        dataIndex: 'advertiserType',
        key: 'advertiserType',
        render: (_, record) => {
          const orgLevel =
            record.createdBy &&
            record.createdBy.roles &&
            record.createdBy.roles[0] &&
            record.createdBy.roles[0].org &&
            record.createdBy.roles[0].org.level;
          const formattedOrgLevel = orgLevel
            ? orgLevel
                .split('_')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')
            : 'N/A';
          return (
            <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>{formattedOrgLevel}</div>
          );
        },
      },
      {
        title: <span style={{ color: '#64748b' }}>Advertiser</span>,
        dataIndex: 'advertiser',
        key: 'advertiser',
        render: (_, record) => {
          const orgName =
            record.createdBy &&
            record.createdBy.roles &&
            record.createdBy.roles[0] &&
            record.createdBy.roles[0].org &&
            record.createdBy.roles[0].org.name;
          return (
            <>
              <Tag
                color="geekblue"
                style={{
                  fontSize: '14px',
                  border: 'none',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '150px',
                }}
              >
                {orgName || 'N/A'}
              </Tag>
            </>
          );
        },
      }
    );
  }

  columns.push({
    title: <span style={{ color: '#64748b' }}>Created</span>,
    dataIndex: 'createdAt',
    key: 'createdAt',
    sorter: true,
    render: (_, record) =>
      record.createdAt ? new Date(record.createdAt).toLocaleDateString() : '',
  });
  columns.push({
    title: <span style={{ color: '#64748b' }}>Last Update</span>,
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    sorter: true,
    render: (_, record) => (
      <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
        <div>{record.updatedAt ? `${getRelativeTime(record.updatedAt)} ` : ''}</div>
        <div style={{ color: 'gray' }}>
          {record.updatedAt
            ? new Date(record.updatedAt).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })
            : ''}
        </div>
      </div>
    ),
  });

  return (
    <>
      {showMyTasks && currentUser.isPreviewOrg && (
        <Row
          justify="center"
          style={{
            padding: '10px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: '4px',
            marginBottom: '15px',
            fontSize: '14px',
            color: '#856404',
          }}
        >
          <span style={{ fontWeight: 'bold', paddingRight: 5 }}>Preview Mode Enabled!</span> No user
          'My Tasks' data is shown. Please switch to All Tasks or Disable the Preview Mode.
        </Row>
      )}
      <TaskRecentActivity />
      <Row justify="end" style={{ marginBottom: 16, gap: '10px' }}>
        <>
          <div>
            <Tooltip placement="top" title={isKanban ? 'Board View' : 'List View'}>
              <Switch
                className="switch"
                checkedChildren="Board"
                unCheckedChildren="List"
                size="default"
                style={{
                  marginTop: '2px',
                  backgroundColor: 'rgba(0, 0, 0, 0.25)',
                  height: '36px',
                  width: '125px',
                  marginRight: '10px',
                }}
                checked={searchParams.kanban ? searchParams.kanban === 'true' : isKanban}
                onChange={(checked) => {
                  setIsKanban(checked);
                  setViewChanged(true);
                  setSearchParams({ kanban: checked });
                }}
              />
            </Tooltip>
          </div>
          <Switch
            checkedChildren="My Tasks"
            unCheckedChildren="All Tasks"
            size="default"
            className="switch"
            style={{
              marginTop: '2px',
              backgroundColor: 'rgba(0, 0, 0, 0.25)',
              height: '36px',
              width: '125px',
              marginRight: '10px',
            }}
            checked={showMyTasks}
            onChange={(change) => {
              setShowMyTasks(change);
              setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
            }}
          />
        </>
        <Input
          placeholder="Search tasks"
          value={searchParams.name || ''}
          onChange={(e) => {
            handleSearchChange(e.target.value);
          }}
          style={{ width: 250, height: '40px', borderRadius: '5px' }}
          prefix={
            <Button
              type="text"
              icon={<CloseOutlined style={{ fontSize: '13px' }} />}
              onClick={() =>
                setSearchParams({
                  ...searchParams,
                  name: '',
                })
              }
            />
          }
        />
        <Button
          onClick={() => {
            history.push(`/tasks/form/`);
          }}
          style={{
            fontSize: '15px',
            height: '41px',
            borderRadius: '5px',
            backgroundColor: '#3b82f6',
            color: 'white',
          }}
        >
          Create Task
        </Button>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }} wrap>
        <Col xs={24} sm={12} md={6} lg={4} xl={4}>
          <div style={{ marginBottom: 8, fontSize: '15px' }}>Assignee</div>
          <Select
            className="custom-select"
            mode="multiple"
            showSearch
            style={{ width: '100%' }}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            filterSort={(optionA, optionB) => {
              if (optionA.value === 'all') return -1;
              if (optionB.value === 'all') return 1;
              return optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase());
            }}
            onSearch={(searchText) => fetchBasicUsers(searchText)}
            loading={basicUsersDetailsLoading}
            onChange={handleAssigneeFilter}
            value={searchParams.assigneeNames ? searchParams.assigneeNames.split(',') : []}
            maxTagCount={1}
            placeholder="Filter by Name"
            allowClear
          >
            {uniqueUsers.map((user) => (
              <Select.Option key={user.id} value={user.id}>
                {startCase(user.name || user.username)}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4} xl={4}>
          <div style={{ marginBottom: 8, fontSize: '15px' }}>Requestor</div>
          <Select
            className="custom-select"
            mode="multiple"
            showSearch
            style={{ width: '100%' }}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            filterSort={(optionA, optionB) => {
              if (optionA.value === 'all') return -1;
              if (optionB.value === 'all') return 1;
              return optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase());
            }}
            onSearch={(searchText) => fetchBasicUsersRequestor(searchText)}
            loading={requestorLoading}
            onChange={handleRequestorFilter}
            value={searchParams.requestor ? searchParams.requestor.split(',') : []}
            maxTagCount={1}
            placeholder="Filter by Requestor"
            allowClear
          >
            {uniqueRequestors.map((user) => (
              <Select.Option key={user.id} value={user.id}>
                {startCase(user.name || user.username)}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4} xl={4}>
          <div style={{ marginBottom: 8, fontSize: '15px' }}>Request Type</div>
          <Select
            mode="multiple"
            className="custom-select"
            style={{ width: '100%' }}
            onChange={handleTaskTypeFilter}
            value={searchParams.requestTypes ? searchParams.requestTypes.split(',') : []}
            maxTagCount={1}
            placeholder="Filter by Request Type"
            allowClear
          >
            {taskTypes.map((type) => (
              <Option key={type} value={type}>
                {type}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4} xl={4}>
          <div style={{ marginBottom: 8, fontSize: '15px' }}>Sub Type</div>
          <Select
            mode="multiple"
            className="custom-select"
            style={{ width: '100%' }}
            onChange={handleTaskSubTypeFilter}
            value={searchParams.requestSubTypes ? searchParams.requestSubTypes.split(',') : []}
            maxTagCount={1}
            placeholder="Filter by Request Subtype"
            allowClear
          >
            {Object.entries(subTypes).map(([mainType, subTypeArray]) => (
              <OptGroup label={mainType} key={mainType}>
                {subTypeArray.map((subType) => (
                  <Option key={`${mainType}-${subType}`} value={subType}>
                    {subType}
                  </Option>
                ))}
              </OptGroup>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4} xl={4}>
          <div style={{ marginBottom: 8, fontSize: '15px' }}>
            Status
            <Tooltip
              title={
                <div style={{ maxWidth: 280 }}>
                  <div style={{ marginBottom: 8 }}>The initial default statuses are:</div>
                  <div style={{ marginBottom: 8, paddingLeft: 12, color: '#a8a8a8' }}>
                    • Not Started <br /> • In Progress <br /> • Ready for Review
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    If you filter other statuses other than the default ones, it will unhide the
                    task of the selected status
                  </div>
                  <div style={{ color: '#a8a8a8', fontStyle: 'italic' }}>
                    Note: To disable the default statuses, please check the checkbox below labeled
                    "Show all statuses"
                  </div>
                </div>
              }
              placement="right"
            >
              <QuestionCircleOutlined
                style={{
                  color: '#8c8c8c',
                  fontSize: '14px',
                  cursor: 'help',
                  marginLeft: '5px',
                }}
              />
            </Tooltip>
          </div>
          <Select
            mode="multiple"
            className="custom-select"
            style={{ width: '100%' }}
            onChange={handleStatusFilter}
            value={searchParams.status ? searchParams.status.split(',') : []}
            maxTagCount={1}
            placeholder="Filter by Status"
            allowClear
          >
            {Object.keys(statusTypes).map((status) => (
              <Option key={status} value={statusTypes[status]}>
                {status}
              </Option>
            ))}
          </Select>
        </Col>
        {level !== 'CHANNEL_PARTNER' && (
          <>
            <Col xs={24} sm={12} md={6} lg={4} xl={4}>
              <div style={{ marginBottom: 8, fontSize: '15px' }}>Advertiser Type</div>
              <Select
                mode="multiple"
                defaultValue="all"
                className="custom-select"
                style={{ width: '100%' }}
                onChange={handleAdvertiserTypeFilter}
                value={searchParams.advertiserTypes ? searchParams.advertiserTypes.split(',') : []}
                maxTagCount={1}
                placeholder="Filter by Advertiser Type"
              >
                {Object.keys(advertiserTypes).map((type) => (
                  <Option key={type} value={advertiserTypes[type]}>
                    {type}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6} lg={4} xl={4}>
              <div style={{ marginBottom: 8, fontSize: '15px' }}>Advertiser</div>
              <Select
                mode="multiple"
                className="custom-select"
                showSearch
                style={{ width: '100%' }}
                onChange={handleAdvertiserFilter}
                onSearch={(searchText) => fetchAdvertiserOrgs(searchText)}
                value={searchParams.advertiser ? searchParams.advertiser.split(',') : []}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                filterSort={(optionA, optionB) => {
                  if (optionA.value === 'all') return -1;
                  if (optionB.value === 'all') return 1;
                  return optionA.children
                    .toLowerCase()
                    .localeCompare(optionB.children.toLowerCase());
                }}
                loading={basicOrgsDetailsLoading}
                maxTagCount={1}
                placeholder="Filter by Advertiser"
                allowClear
              >
                {basicOrgsDetails.map((org) => (
                  <Option key={org.id} value={org.name}>
                    {org.name}
                  </Option>
                ))}
              </Select>
            </Col>
          </>
        )}
        <Col xs={24} sm={12} md={6} lg={4} xl={4}>
          <div style={{ marginBottom: 8, fontSize: '15px' }}>Urgency</div>
          <Select
            mode="multiple"
            className="custom-select"
            style={{ width: '100%' }}
            onChange={handleUrgencyFilter}
            value={searchParams.urgency ? searchParams.urgency.split(',') : []}
            maxTagCount={1}
            placeholder="Filter by Urgency"
            allowClear
          >
            {Object.keys(urgencyTypes).map((urgency) => (
              <Option key={urgency} value={urgencyTypes[urgency]}>
                <Badge color={urgencyColors[urgencyTypes[urgency]]} style={{ marginRight: 8 }} />
                {urgency}
              </Option>
            ))}
          </Select>
        </Col>
        {/* Due Date Filter */}
        <Col xs={24} sm={12} md={6} lg={4} xl={4}>
          <div style={{ marginBottom: 8, fontSize: '15px' }}>Due Date</div>
          <DatePicker.RangePicker
            className="custom-date"
            style={{
              borderRadius: '5px',
              display: 'flex',
              alignItems: 'center',
              minHeight: '40px',
              color: 'black',
            }}
            value={
              searchParams.dueDate
                ? searchParams.dueDate.split(',').length === 2
                  ? [
                      moment(searchParams.dueDate.split(',')[0], 'YYYY-MM-DD'),
                      moment(searchParams.dueDate.split(',')[1], 'YYYY-MM-DD'),
                    ]
                  : []
                : []
            }
            onChange={(dates) =>
              handleDueDateFilter(
                dates && dates.length === 2
                  ? dates.map((d) => d.format('YYYY-MM-DD')).join(',')
                  : ''
              )
            }
            placeholder={['From', 'To']}
            allowClear
            format="YYYY-MM-DD"
          />
        </Col>
      </Row>

      {!isKanban && (
        <Row style={{ marginTop: 16, marginBottom: 8 }}>
          <Col>
            <Checkbox
              checked={
                searchParams.showAllStatuses === true || searchParams.showAllStatuses === 'true'
              }
              onChange={(e) => {
                const updatedFilters = {
                  ...searchParams,
                  showAllStatuses: e.target.checked,
                };
                setSearchParams(updatedFilters);
                setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
              }}
              style={{ fontSize: '15px' }}
            >
              Show all statuses
            </Checkbox>
            <Tooltip
              title={
                <div style={{ maxWidth: 280 }}>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Unchecked:</strong> Shows only tasks with default statuses
                  </div>
                  <div style={{ marginBottom: 8, paddingLeft: 12, color: '#a8a8a8' }}>
                    • Not Started <br /> • In Progress <br /> • Ready for Review
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Checked:</strong> Shows tasks with all statuses
                  </div>
                  <div style={{ color: '#8c8c8c', fontStyle: 'italic' }}>
                    Note: Save filters to keep your preferences
                  </div>
                </div>
              }
              placement="right"
            >
              <QuestionCircleOutlined
                style={{
                  color: '#8c8c8c',
                  fontSize: '14px',
                  cursor: 'help',
                }}
              />
            </Tooltip>
          </Col>
        </Row>
      )}

      {/* CLEAR FILTERS / SAVE-LOAD FILTERING / Task Filter */}
      <Row style={{ marginTop: 30, paddingBottom: 16 }} wrap>
        <Col
          style={{
            marginRight: 'auto',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {showMyTasks && (
            <Col>
              <Dropdown
                overlay={
                  <Card>
                    <Checkbox.Group
                      options={filterMenuColumnOptions.filter(
                        (option) => level === 'PLATFORM' || option.value !== 'collaborating'
                      )}
                      value={visibleColumns}
                      onChange={(values) => {
                        const updatedFilters = {
                          ...searchParams,
                          following: values.includes('following'),
                          collaborating: values.includes('collaborating'),
                        };
                        setVisibleColumns(values);
                        setSearchParams(updatedFilters);
                      }}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '8px',
                      }}
                    />
                  </Card>
                }
                trigger={['click']}
                placement="bottomRight"
              >
                <Button
                  style={{
                    marginRight: 60,
                    width: '100%',
                    height: '35px',
                    borderRadius: '5px',
                  }}
                >
                  My Tasks Filter <DownOutlined />
                </Button>
              </Dropdown>
            </Col>
          )}
        </Col>
        <Row style={{ marginRight: 16 }}>
          <Button
            style={{
              fontSize: '15px',
              height: '35px',
              borderRadius: '5px',
              marginRight: 8,
            }}
            disabled={
              !searchParams ||
              !Object.values(searchParams).some((param) => param && param !== 'false')
            }
            onClick={() => {
              const clearedParams = {
                assigneeNames: '',
                requestor: '',
                requestTypes: '',
                requestSubTypes: '',
                status: '',
                advertiserTypes: '',
                advertiser: '',
                name: '',
                urgency: '',
                dueDate: '',
                following: false,
                collaborating: false,
                showAllStatuses: false,
              };
              // Clear the state first
              setSearchParams(clearedParams);
              setVisibleColumns([]);
              setTaskBoardPagination((prev) => ({
                ...prev,
                current: 1,
              }));
              handleFetchTasksPaginated({
                skip: 0,
                first: taskBoardPagination.pageSize,
                ...clearedParams,
              });
            }}
            icon={<CloseOutlined style={{ fontSize: '13px' }} />}
          >
            Clear Filters
          </Button>
        </Row>
        <Button
          onClick={handleLoadFilters}
          style={{
            fontSize: '15px',
            height: '35px',
            borderRadius: '5px',
            marginRight: 8,
          }}
        >
          Load Filters
        </Button>
        <Button
          onClick={handleSaveFilters}
          type="primary"
          style={{
            fontSize: '15px',
            height: '35px',
            borderRadius: '5px',
            backgroundColor: '#3b82f6',
            color: 'white',
          }}
        >
          Save Filters
        </Button>
      </Row>

      {isKanban ? (
        <Kanban
          // Always pass all tasks in Kanban mode (filtered by showMyTasks in query)
          tasks={Array.isArray(task) ? task : []}
          showAllTasks={!showMyTasks}
        />
      ) : (
        <>
          <Table
            columns={columns}
            dataSource={task}
            loading={tasksLoading}
            pagination={false}
            scroll={{ x: 1000 }}
            className="custom-table"
            onChange={handleTableChange}
          />
          <Pagination
            current={taskBoardPagination.current}
            pageSize={taskBoardPagination.pageSize}
            total={taskBoardPagination.total}
            showSizeChanger
            hideOnSinglePage={false}
            onChange={(page, pageSize) => handlePaginationChange({ page, pageSize })}
            onShowSizeChange={handlePaginationSizeChange}
            style={{ marginTop: 16, paddingBottom: 16 }}
            itemRender={itemRender}
            className="custom-pagination"
          />
        </>
      )}
    </>
  );
};

export default withRouter(TaskBoard);
